import { z } from 'zod'
import type { IrrigationPartKind } from './irrigation-part-kind.enum.js'

/*
 * The vocabulary of a sprinkler system, mirroring the enums of the same names
 * in prisma/schema.prisma. One file because they are one vocabulary: a change
 * to what can be recorded usually touches several of them at once. The words
 * are the operator's (docs/irrigation.md, "Words").
 *
 * Member order is the order pickers show. Prisma may list the same members
 * in a different order. Parity checks the set, not the sequence.
 */

/** Mirrors `WaterSourceKind`. */
export const WATER_SOURCE_KINDS = ['CITY_METER', 'WELL'] as const
export const waterSourceKindSchema = z.enum(WATER_SOURCE_KINDS)
export type WaterSourceKind = z.infer<typeof waterSourceKindSchema>
export const WATER_SOURCE_KIND_LABELS: Record<WaterSourceKind, string> = {
  CITY_METER: 'City meter',
  WELL: 'Well',
}

/** Mirrors `IrrigationEquipmentKind`. Timers and water sources are their own records. */
export const IRRIGATION_EQUIPMENT_KINDS = [
  'VALVE',
  'VALVE_BOX',
  'MASTER_VALVE',
  'BACKFLOW',
  'BALL_VALVE',
  'SENSOR',
  'WELL_COMPONENT',
  'OTHER',
] as const
export const irrigationEquipmentKindSchema = z.enum(IRRIGATION_EQUIPMENT_KINDS)
export type IrrigationEquipmentKind = z.infer<
  typeof irrigationEquipmentKindSchema
>
export const IRRIGATION_EQUIPMENT_KIND_LABELS: Record<
  IrrigationEquipmentKind,
  string
> = {
  VALVE: 'Valve',
  VALVE_BOX: 'Valve box',
  MASTER_VALVE: 'Master valve',
  BACKFLOW: 'Backflow',
  BALL_VALVE: 'Ball valve',
  SENSOR: 'Sensor',
  WELL_COMPONENT: 'Well component',
  OTHER: 'Other',
}

/**
 * What can sit in a valve box (docs/irrigation.md, "Valve boxes"). A backflow
 * and a well's parts stand where they are, and a box is not put in a box.
 */
export const BOXABLE_EQUIPMENT_KINDS = [
  'VALVE',
  'MASTER_VALVE',
  'BALL_VALVE',
  'SENSOR',
  'OTHER',
] as const satisfies readonly IrrigationEquipmentKind[]
export type BoxableEquipmentKind = (typeof BOXABLE_EQUIPMENT_KINDS)[number]

export function isBoxableKind(
  kind: IrrigationEquipmentKind,
): kind is BoxableEquipmentKind {
  return BOXABLE_EQUIPMENT_KINDS.some((boxable) => boxable === kind)
}

/**
 * What a water source can be recorded as having none of: the equipment the
 * water passes through between the source and its timers. "None here" is a
 * fact of its own, apart from "not recorded yet".
 */
export const SOURCE_ABSENT_EQUIPMENT = [
  'BALL_VALVE',
  'BACKFLOW',
  'MASTER_VALVE',
] as const satisfies readonly IrrigationEquipmentKind[]
export const sourceAbsentEquipmentSchema = z.enum(SOURCE_ABSENT_EQUIPMENT)
export type SourceAbsentEquipment = z.infer<typeof sourceAbsentEquipmentSchema>

/** What a timer can be recorded as having none of. */
export const TIMER_ABSENT_EQUIPMENT = [
  'SENSOR',
] as const satisfies readonly IrrigationEquipmentKind[]
export const timerAbsentEquipmentSchema = z.enum(TIMER_ABSENT_EQUIPMENT)
export type TimerAbsentEquipment = z.infer<typeof timerAbsentEquipmentSchema>

/**
 * Which part picker each kind of equipment reads from. A master valve is
 * picked from the valves; an OTHER has no picker of its own and offers every
 * irrigation material.
 */
export const PART_KIND_FOR_EQUIPMENT: Record<
  IrrigationEquipmentKind,
  IrrigationPartKind | null
> = {
  VALVE: 'VALVE',
  VALVE_BOX: 'VALVE_BOX',
  MASTER_VALVE: 'VALVE',
  BACKFLOW: 'BACKFLOW',
  BALL_VALVE: 'BALL_VALVE',
  SENSOR: 'SENSOR',
  WELL_COMPONENT: 'WELL_COMPONENT',
  OTHER: null,
}

/**
 * Mirrors `PipeSize`: nominal inches. Prisma enum names cannot start with a
 * digit, hence `IN_`; the labels are what anybody reads.
 */
export const PIPE_SIZES = [
  'IN_1_2',
  'IN_5_8',
  'IN_3_4',
  'IN_1',
  'IN_1_1_4',
  'IN_1_1_2',
  'IN_2',
  'IN_2_1_2',
  'IN_3',
  'IN_4',
] as const
export const pipeSizeSchema = z.enum(PIPE_SIZES)
export type PipeSize = z.infer<typeof pipeSizeSchema>
export const PIPE_SIZE_LABELS: Record<PipeSize, string> = {
  IN_1_2: '½"',
  IN_5_8: '⅝"',
  IN_3_4: '¾"',
  IN_1: '1"',
  IN_1_1_4: '1¼"',
  IN_1_1_2: '1½"',
  IN_2: '2"',
  IN_2_1_2: '2½"',
  IN_3: '3"',
  IN_4: '4"',
}

/**
 * The sizes a zone or master valve comes in, from the operator's list.
 * Each is a `PipeSize`: the column is that enum, so a size named here that
 * the column cannot store would be accepted by the input and refused by the
 * database.
 */
export const VALVE_SIZES = [
  'IN_3_4',
  'IN_1',
  'IN_1_1_2',
  'IN_2',
  'IN_3',
  'IN_4',
] as const satisfies readonly PipeSize[]
export const valveSizeSchema = z.enum(VALVE_SIZES)
export type ValveSize = z.infer<typeof valveSizeSchema>

/** City meters run from ⅝" (most homes) up; ⅝" exists only here. */
export const METER_SIZES = [
  'IN_5_8',
  'IN_3_4',
  'IN_1',
  'IN_1_1_2',
  'IN_2',
  'IN_3',
  'IN_4',
] as const satisfies readonly PipeSize[]
export const meterSizeSchema = z.enum(METER_SIZES)
export type MeterSize = z.infer<typeof meterSizeSchema>

/** Ball valves and backflows: every pipe size but a meter's ⅝". */
export const LINE_SIZES = [
  'IN_1_2',
  'IN_3_4',
  'IN_1',
  'IN_1_1_4',
  'IN_1_1_2',
  'IN_2',
  'IN_2_1_2',
  'IN_3',
  'IN_4',
] as const satisfies readonly PipeSize[]
export const lineSizeSchema = z.enum(LINE_SIZES)
export type LineSize = z.infer<typeof lineSizeSchema>

/** Mirrors `BackflowType`. */
export const BACKFLOW_TYPES = ['RPZ', 'PVB', 'DCVA', 'SVB'] as const
export const backflowTypeSchema = z.enum(BACKFLOW_TYPES)
export type BackflowType = z.infer<typeof backflowTypeSchema>
export const BACKFLOW_TYPE_LABELS: Record<BackflowType, string> = {
  RPZ: 'RPZ (reduced pressure)',
  PVB: 'PVB (pressure vacuum breaker)',
  DCVA: 'DCVA (double check)',
  SVB: 'SVB (spill-resistant)',
}

/** Mirrors `SensorType`. */
export const SENSOR_TYPES = [
  'RAIN',
  'FREEZE',
  'RAIN_FREEZE',
  'SOIL_MOISTURE',
  'FLOW',
] as const
export const sensorTypeSchema = z.enum(SENSOR_TYPES)
export type SensorType = z.infer<typeof sensorTypeSchema>
export const SENSOR_TYPE_LABELS: Record<SensorType, string> = {
  RAIN: 'Rain',
  FREEZE: 'Freeze',
  RAIN_FREEZE: 'Rain/freeze',
  SOIL_MOISTURE: 'Soil moisture',
  FLOW: 'Flow',
}

/** Mirrors `WellComponentType`. */
export const WELL_COMPONENT_TYPES = [
  'PUMP',
  'PRESSURE_TANK',
  'PRESSURE_SWITCH',
  'PUMP_START_RELAY',
  'CHECK_VALVE',
  'FILTER',
  'OTHER',
] as const
export const wellComponentTypeSchema = z.enum(WELL_COMPONENT_TYPES)
export type WellComponentType = z.infer<typeof wellComponentTypeSchema>
export const WELL_COMPONENT_TYPE_LABELS: Record<WellComponentType, string> = {
  PUMP: 'Pump',
  PRESSURE_TANK: 'Pressure tank',
  PRESSURE_SWITCH: 'Pressure switch',
  PUMP_START_RELAY: 'Pump start relay',
  CHECK_VALVE: 'Check valve',
  FILTER: 'Filter',
  OTHER: 'Other',
}

/** Mirrors `HeadType`: what a zone's heads are counted by. */
export const HEAD_TYPES = [
  'SPRAY',
  'ROTOR',
  'ROTARY_NOZZLE',
  'IMPACT',
  'BUBBLER',
  'DRIP',
] as const
export const headTypeSchema = z.enum(HEAD_TYPES)
export type HeadType = z.infer<typeof headTypeSchema>
export const HEAD_TYPE_LABELS: Record<HeadType, string> = {
  SPRAY: 'Spray',
  ROTOR: 'Rotor',
  ROTARY_NOZZLE: 'Rotary nozzle',
  IMPACT: 'Impact',
  BUBBLER: 'Bubbler',
  DRIP: 'Drip',
}

/**
 * Mirrors `IrrigationProblemSubject`: what a kind of problem can be recorded
 * on. A zone with a pin (a broken riser) is a different subject from a zone as
 * a whole (a weeping diaphragm).
 */
export const IRRIGATION_PROBLEM_SUBJECTS = [
  'ZONE_SPOT',
  'ZONE',
  'TIMER',
  'VALVE',
  'BACKFLOW',
  'BALL_VALVE',
  'SENSOR',
  'WELL_COMPONENT',
  'VALVE_BOX',
  'WATER_SOURCE',
  'OTHER_EQUIPMENT',
] as const
export const irrigationProblemSubjectSchema = z.enum(
  IRRIGATION_PROBLEM_SUBJECTS,
)
export type IrrigationProblemSubject = z.infer<
  typeof irrigationProblemSubjectSchema
>
export const IRRIGATION_PROBLEM_SUBJECT_LABELS: Record<
  IrrigationProblemSubject,
  string
> = {
  ZONE_SPOT: 'A spot in a zone',
  ZONE: 'A zone as a whole',
  TIMER: 'A timer',
  VALVE: 'A valve',
  BACKFLOW: 'A backflow',
  BALL_VALVE: 'A ball valve',
  SENSOR: 'A sensor',
  WELL_COMPONENT: 'A well component',
  VALVE_BOX: 'A valve box',
  WATER_SOURCE: 'A water source',
  OTHER_EQUIPMENT: 'Other equipment',
}

/** The problem subject a piece of equipment is, by its kind. A master valve is a valve. */
export const PROBLEM_SUBJECT_FOR_EQUIPMENT: Record<
  IrrigationEquipmentKind,
  IrrigationProblemSubject
> = {
  VALVE: 'VALVE',
  VALVE_BOX: 'VALVE_BOX',
  MASTER_VALVE: 'VALVE',
  BACKFLOW: 'BACKFLOW',
  BALL_VALVE: 'BALL_VALVE',
  SENSOR: 'SENSOR',
  WELL_COMPONENT: 'WELL_COMPONENT',
  OTHER: 'OTHER_EQUIPMENT',
}
